import React, { useEffect, useMemo, useState, useCallback } from "react";
import ProductList from "../productList/ProductList";
import NotFoundProduct from "../notFoundProduct/NotFoundProduct";
import { useProducts } from "../../apiHooks/useProduct";
import { useSearchParams, useNavigate } from "react-router-dom";
import useCategory from "../../apiHooks/useCategory";
import { useBrands } from "../../apiHooks/useBrand";
import imgUrl from "../../imgUrl";
import { useTranslation } from "react-i18next";

/* ─── Loading skeleton ─────────────────────────────── */
const CardSkeleton = () => (
  <div className="bg-white rounded-lg overflow-hidden shadow-sm animate-pulse">
    <div className="bg-gray-200 h-44 w-full" />
    <div className="p-3 space-y-2">
      <div className="h-3 bg-gray-200 rounded w-3/4" />
      <div className="h-3 bg-gray-200 rounded w-1/2" />
      <div className="h-8 bg-gray-200 rounded w-full mt-2" />
    </div>
  </div>
);

/* ─── Main Component ───────────────────────────────── */
const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const SORT_OPTIONS = [
    { value: "created_at:desc", label: t('shop.sortNewest') },
    { value: "price:asc",       label: t('shop.sortPriceLow') },
    { value: "price:desc",      label: t('shop.sortPriceHigh') },
    { value: "name:asc",        label: t('shop.sortNameAZ') },
  ];

  const searchTerm    = searchParams.get("search")      || "";
  const categoryId    = searchParams.get("category_id") || "";
  const sortParam     = searchParams.get("sort")        || "created_at:desc";
  const minPriceParam = searchParams.get("min_price")   || "";
  const maxPriceParam = searchParams.get("max_price")   || "";
  const brandIdParam  = searchParams.get("brand_id")    || "";
  const onSaleParam   = searchParams.get("on_sale")     || "";

  const [page, setPage] = useState(1);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // grid | list

  const [minPrice, setMinPrice] = useState(minPriceParam);
  const [maxPrice, setMaxPrice] = useState(maxPriceParam);

  // Sync state if searchParams change externally (e.g. back button or clearing chips)
  useEffect(() => {
    setMinPrice(minPriceParam);
  }, [minPriceParam]);

  useEffect(() => {
    setMaxPrice(maxPriceParam);
  }, [maxPriceParam]);

  // Reset page when search params change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, categoryId, sortParam, minPriceParam, maxPriceParam, brandIdParam, onSaleParam]);

  const [sort_by, sort_order] = sortParam.split(":");

  const params = useMemo(() => ({
    search:      searchTerm    || undefined,
    category_id: categoryId    || undefined,
    brand_id:    brandIdParam  || undefined,
    min_price:   minPriceParam || undefined,
    max_price:   maxPriceParam || undefined,
    on_sale:     onSaleParam === "true" ? true : undefined,
    sort_by,
    sort_order,
    paginate:    true,
    per_page:    20,
    page,
  }), [searchTerm, categoryId, brandIdParam, minPriceParam, maxPriceParam, onSaleParam, sort_by, sort_order, page]);

  const { data, isLoading, isFetching } = useProducts(params);
  const { data: categories = [], isLoading: catLoading } = useCategory();
  const { data: brands = [], isLoading: brandsLoading } = useBrands();

  const handleBrandToggle = (id) => {
    const p = new URLSearchParams(searchParams);
    let currentBrands = brandIdParam ? brandIdParam.split(",") : [];
    if (currentBrands.includes(String(id))) {
      currentBrands = currentBrands.filter(b => b !== String(id));
    } else {
      currentBrands.push(String(id));
    }
    if (currentBrands.length > 0) {
      p.set("brand_id", currentBrands.join(","));
    } else {
      p.delete("brand_id");
    }
    p.delete("page");
    setSearchParams(p);
  };

  const handleOnSaleToggle = (checked) => {
    const p = new URLSearchParams(searchParams);
    if (checked) {
      p.set("on_sale", "true");
    } else {
      p.delete("on_sale");
    }
    p.delete("page");
    setSearchParams(p);
  };

  const handlePriceApply = () => {
    const p = new URLSearchParams(searchParams);
    if (minPrice) {
      p.set("min_price", minPrice);
    } else {
      p.delete("min_price");
    }
    if (maxPrice) {
      p.set("max_price", maxPrice);
    } else {
      p.delete("max_price");
    }
    p.delete("page");
    setSearchParams(p);
  };

  const items    = data?.items  ?? [];
  const meta     = data?.meta;
  const total    = meta?.total  ?? items.length;
  const lastPage = meta?.last_page ?? 1;

  // Build category hierarchy
  const parentCategories = useMemo(() =>
    categories.filter((c) => !c.parent_id),
    [categories]
  );
  const childMap = useMemo(() =>
    categories.reduce((acc, c) => {
      if (c.parent_id) {
        acc[c.parent_id] = acc[c.parent_id] || [];
        acc[c.parent_id].push(c);
      }
      return acc;
    }, {}),
    [categories]
  );
  const isHierarchical = categories.some((c) => c.parent_id);
  const topLevelCategories = isHierarchical ? parentCategories : categories;

  // Filter top-level + any matching children by search term
  const filteredTopCategories = useMemo(() => {
    if (!categorySearch) return topLevelCategories;
    const q = categorySearch.toLowerCase();
    return topLevelCategories.filter(c => {
      const children = childMap[c.id] || c.sub_categories || [];
      return c.name.toLowerCase().includes(q) ||
        children.some(s => s.name.toLowerCase().includes(q));
    });
  }, [topLevelCategories, childMap, categorySearch]);

  // Filter in-stock client-side (computed inside useMemo to avoid stale closure)
  const displayItems = useMemo(() => {
    const list = data?.items ?? [];
    return inStockOnly ? list.filter(p => p.stock_quantity > 0) : list;
  }, [data?.items, inStockOnly]);

  const handleCategoryClick = useCallback((id) => {
    const p = new URLSearchParams(searchParams);
    if (categoryId === String(id)) {
      p.delete("category_id");
    } else {
      p.set("category_id", id);
    }
    p.delete("search");
    setSearchParams(p);
  }, [searchParams, categoryId, setSearchParams]);

  const handleSortChange = (val) => {
    const p = new URLSearchParams(searchParams);
    p.set("sort", val);
    setSearchParams(p);
  };

  const clearCategory = () => {
    const p = new URLSearchParams(searchParams);
    p.delete("category_id");
    setSearchParams(p);
  };

  const clearSearch = () => {
    const p = new URLSearchParams(searchParams);
    p.delete("search");
    setSearchParams(p);
  };

  // Find active category across all categories (parents + children)
  const activeCategory = categories.find(c => String(c.id) === categoryId);

  const loading = isLoading || isFetching;

  /* ─── Sidebar ─────────────────────────────────── */
  const Sidebar = ({ mobile = false }) => (
    <aside className={mobile
      ? "fixed inset-0 z-50 bg-white overflow-y-auto p-4"
      : "w-64 flex-shrink-0 hidden lg:block"
    }>
      {mobile && (
        <div className="flex items-center justify-between mb-4 pb-3 border-b">
          <h2 className="font-bold text-lg text-gray-800">{t('shop.filters')}</h2>
          <button
            onClick={() => setMobileFilterOpen(false)}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Category filter */}
      <div className={mobile ? "" : "sticky top-24"}>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-sm text-gray-800 uppercase tracking-wide">{t('shop.categories')}</h3>
          </div>
          <div className="p-3">
            <div className="relative mb-3">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={categorySearch}
                onChange={e => setCategorySearch(e.target.value)}
                placeholder={t('shop.searchCategory')}
                className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-400 bg-gray-50"
              />
            </div>
            <div className="max-h-80 overflow-y-auto space-y-0.5 scrollbar-thin scrollbar-thumb-gray-200">
              {/* All products */}
              <button
                onClick={clearCategory}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors duration-150 flex items-center ${
                  !categoryId
                    ? "bg-emerald-500 text-white font-medium"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <span>{t('shop.allProducts')}</span>
              </button>

              {catLoading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="h-9 bg-gray-100 rounded-lg animate-pulse mx-1 mb-1" />
                  ))
                : filteredTopCategories.map((cat) => {
                    const children = (childMap[cat.id] || cat.sub_categories || []).filter(s =>
                      !categorySearch || s.name.toLowerCase().includes(categorySearch.toLowerCase())
                    );
                    const isActiveParent = String(cat.id) === categoryId;
                    const hasActiveChild = children.some(s => String(s.id) === categoryId);
                    const isExpanded = isActiveParent || hasActiveChild || !!categorySearch;

                    return (
                      <div key={cat.id}>
                        {/* Parent row */}
                        <button
                          onClick={() => handleCategoryClick(cat.id)}
                          className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors duration-150 flex items-center gap-2 ${
                            isActiveParent
                              ? "bg-emerald-500 text-white font-medium"
                              : "text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
                          }`}
                        >
                          {cat.image && (
                            <img
                              src={`${imgUrl}/${cat.image}`}
                              alt=""
                              className="w-4 h-4 object-contain flex-shrink-0"
                              onError={e => { e.target.style.display = "none"; }}
                            />
                          )}
                          <span className="truncate flex-1">{cat.name}</span>
                          {children.length > 0 && (
                            <svg className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          )}
                        </button>

                        {/* Children rows */}
                        {children.length > 0 && isExpanded && (
                          <div className="ml-3 pl-3 border-l-2 border-gray-100 mt-0.5 mb-1 space-y-0.5">
                            {children.map((sub) => (
                              <button
                                key={sub.id}
                                onClick={() => handleCategoryClick(sub.id)}
                                className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors duration-150 flex items-center gap-2 ${
                                  String(sub.id) === categoryId
                                    ? "bg-emerald-500 text-white font-medium"
                                    : "text-gray-600 hover:bg-emerald-50 hover:text-emerald-700"
                                }`}
                              >
                                <svg className="w-3 h-3 flex-shrink-0 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <span className="truncate">{sub.name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
            </div>
          </div>
        </div>

        {/* Stock filter */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mt-4">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-sm text-gray-800 uppercase tracking-wide">{t('shop.availability')}</h3>
          </div>
          <div className="p-4">
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={inStockOnly}
                  onChange={e => setInStockOnly(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-10 h-5 bg-gray-200 peer-checked:bg-emerald-500 rounded-full transition-colors duration-200" />
                <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 peer-checked:translate-x-5" />
              </div>
              <span className="text-sm text-gray-700 group-hover:text-emerald-600">{t('shop.inStockOnly')}</span>
            </label>
          </div>
        </div>

        {/* Price filter */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mt-4">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-sm text-gray-800 uppercase tracking-wide">{t('shop.priceRange', 'Price Range')}</h3>
          </div>
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={minPrice}
                onChange={e => setMinPrice(e.target.value)}
                placeholder={t('shop.minPrice', 'Min')}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-400 bg-gray-50 text-gray-800"
              />
              <span className="text-gray-400 text-xs">-</span>
              <input
                type="number"
                value={maxPrice}
                onChange={e => setMaxPrice(e.target.value)}
                placeholder={t('shop.maxPrice', 'Max')}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-400 bg-gray-50 text-gray-800"
              />
            </div>
            <button
              onClick={handlePriceApply}
              className="w-full py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-semibold transition"
            >
              {t('shop.applyPrice', 'Apply')}
            </button>
          </div>
        </div>

        {/* Brand filter */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mt-4">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-sm text-gray-800 uppercase tracking-wide">{t('shop.brands', 'Brands')}</h3>
          </div>
          <div className="p-3">
            {brandsLoading ? (
              <div className="space-y-2">
                <div className="h-6 bg-gray-100 rounded animate-pulse" />
                <div className="h-6 bg-gray-100 rounded animate-pulse" />
                <div className="h-6 bg-gray-100 rounded animate-pulse" />
              </div>
            ) : brands.length === 0 ? (
              <p className="text-gray-500 text-xs px-2">{t('shop.noBrands', 'No brands found')}</p>
            ) : (
              <div className="max-h-48 overflow-y-auto space-y-1.5 scrollbar-thin scrollbar-thumb-gray-200 px-1">
                {brands.map((brand) => {
                  const isChecked = brandIdParam.split(",").includes(String(brand.id));
                  return (
                    <label key={brand.id} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 hover:text-emerald-600 transition duration-150">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleBrandToggle(brand.id)}
                        className="rounded text-emerald-500 focus:ring-emerald-400 border-gray-300 w-4 h-4 cursor-pointer"
                      />
                      <span className="truncate">{brand.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* On Sale filter */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mt-4">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-sm text-gray-800 uppercase tracking-wide">{t('shop.deals', 'Deals')}</h3>
          </div>
          <div className="p-4">
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={onSaleParam === "true"}
                  onChange={e => handleOnSaleToggle(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-10 h-5 bg-gray-200 peer-checked:bg-emerald-500 rounded-full transition-colors duration-200" />
                <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 peer-checked:translate-x-5" />
              </div>
              <span className="text-sm text-gray-700 group-hover:text-emerald-600">{t('shop.onSaleOnly', 'On Sale Only')}</span>
            </label>
          </div>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 py-8">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-white">
                {activeCategory
                  ? activeCategory.name
                  : searchTerm
                  ? `${t('shop.resultsFor')} "${searchTerm}"`
                  : t('shop.allProducts')}
              </h1>
              <p className="text-emerald-100 text-sm mt-1">
                {loading ? t('shop.loading') : `${total.toLocaleString()} ${t('shop.productsFound')}`}
              </p>
            </div>
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-emerald-100">
              <button onClick={() => navigate("/")} className="hover:text-white">{t('shop.home')}</button>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-white font-medium">
                {activeCategory ? activeCategory.name : searchTerm ? t('shop.search') : t('shop.shop')}
              </span>
            </nav>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-10 py-8">
        <div className="flex gap-6">
          {/* Sidebar — Desktop */}
          <Sidebar />

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Active filter chips */}
            {(searchTerm || categoryId || minPriceParam || maxPriceParam || brandIdParam || onSaleParam) && (
              <div className="flex flex-wrap gap-2 mb-4">
                {searchTerm && (
                  <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 text-sm font-medium px-3 py-1.5 rounded-full">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    {searchTerm}
                    <button onClick={clearSearch} className="ml-1 hover:text-red-500">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                )}
                {activeCategory && (
                  <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 text-sm font-medium px-3 py-1.5 rounded-full">
                    {activeCategory.name}
                    <button onClick={clearCategory} className="ml-1 hover:text-red-500">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                )}
                {(minPriceParam || maxPriceParam) && (
                  <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 text-sm font-medium px-3 py-1.5 rounded-full">
                    Price: {minPriceParam ? `$${minPriceParam}` : '$0'} - {maxPriceParam ? `$${maxPriceParam}` : '∞'}
                    <button onClick={() => {
                      const p = new URLSearchParams(searchParams);
                      p.delete("min_price");
                      p.delete("max_price");
                      setSearchParams(p);
                    }} className="ml-1 hover:text-red-500">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                )}
                {brandIdParam && brandIdParam.split(",").map(bId => {
                  const brand = brands.find(b => String(b.id) === bId);
                  if (!brand) return null;
                  return (
                    <span key={bId} className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 text-sm font-medium px-3 py-1.5 rounded-full">
                      {brand.name}
                      <button onClick={() => handleBrandToggle(bId)} className="ml-1 hover:text-red-500">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  );
                })}
                {onSaleParam === "true" && (
                  <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 text-sm font-medium px-3 py-1.5 rounded-full">
                    {t('shop.onSaleOnly', 'On Sale Only')}
                    <button onClick={() => handleOnSaleToggle(false)} className="ml-1 hover:text-red-500">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                )}
              </div>
            )}

            {/* Toolbar */}
            <div className="flex items-center justify-between bg-white border border-gray-100 rounded-xl px-4 py-3 mb-5 shadow-sm gap-3 flex-wrap">
              {/* Left: mobile filter + count */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setMobileFilterOpen(true)}
                  className="lg:hidden flex items-center gap-2 text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                  </svg>
                  {t('shop.filters')}
                </button>
                <span className="text-sm text-gray-500">
                  {t('shop.showing')} <span className="font-semibold text-gray-800">{displayItems.length}</span>
                  {meta?.total ? ` ${t('shop.of')} ${meta.total}` : ""} {t('shop.products')}
                </span>
              </div>

              {/* Right: sort + view toggle */}
              <div className="flex items-center gap-3">
                <select
                  value={sortParam}
                  onChange={e => handleSortChange(e.target.value)}
                  className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-emerald-400 cursor-pointer text-gray-700"
                >
                  {SORT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>

                {/* Grid/List toggle */}
                <div className="hidden sm:flex items-center border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-2 ${viewMode === "grid" ? "bg-emerald-500 text-white" : "text-gray-500 hover:bg-gray-50"}`}
                    title="Grid view"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-2 ${viewMode === "list" ? "bg-emerald-500 text-white" : "text-gray-500 hover:bg-gray-50"}`}
                    title="List view"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Product Grid */}
            {loading ? (
              <div className={
                viewMode === "grid"
                  ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4"
                  : "flex flex-col gap-3"
              }>
                {Array.from({ length: 20 }).map((_, i) => <CardSkeleton key={i} />)}
              </div>
            ) : displayItems.length > 0 ? (
              <>
                <ProductList
                  data={displayItems}
                  meta={meta}
                  currentPage={page}
                  onPageChange={setPage}
                  viewMode={viewMode}
                />
              </>
            ) : (
              <NotFoundProduct />
            )}

            {/* Server-side pagination */}
            {!loading && lastPage > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8 flex-wrap">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  {t('shop.previous')}
                </button>
                {Array.from({ length: Math.min(lastPage, 7) }, (_, i) => {
                  let p;
                  if (lastPage <= 7) {
                    p = i + 1;
                  } else if (page <= 4) {
                    p = i + 1;
                  } else if (page >= lastPage - 3) {
                    p = lastPage - 6 + i;
                  } else {
                    p = page - 3 + i;
                  }
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition ${
                        page === p
                          ? "bg-emerald-500 text-white shadow-sm"
                          : "border border-gray-200 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-300"
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
                <button
                  disabled={page >= lastPage}
                  onClick={() => setPage(p => Math.min(lastPage, p + 1))}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  {t('shop.next')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile filter drawer */}
      {mobileFilterOpen && (
        <div className="lg:hidden">
          <div
            className="fixed inset-0 z-40 bg-black bg-opacity-40 backdrop-blur-sm"
            onClick={() => setMobileFilterOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 w-80 max-w-full bg-white shadow-xl overflow-y-auto">
            <Sidebar mobile />
          </div>
        </div>
      )}
    </div>
  );
};

export default Search;
